/**
 * OfflineBanner - shown when the browser is disconnected.
 *
 * Queues mutations locally and replays them when reconnected.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToastStore } from '../../stores/toast-store';
import { replayMutations, getPendingMutationCount } from '../../lib/mutation-queue';

export function OfflineBanner() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [pendingCount, setPendingCount] = useState(0);
  const [isReplaying, setIsReplaying] = useState(false);
  const addToast = useToastStore((s) => s.addToast);

  const handleOnline = useCallback(async () => {
    setIsOnline(true);
    const count = getPendingMutationCount();
    if (count === 0) return;

    setIsReplaying(true);
    try {
      const replayed = await replayMutations();
      if (replayed > 0) {
        addToast({ message: `Synced ${replayed} offline change${replayed !== 1 ? 's' : ''}.`, type: 'success' });
      }
    } catch {
      addToast({ message: 'Some offline changes failed to sync.', type: 'error' });
    } finally {
      setIsReplaying(false);
      setPendingCount(0);
    }
  }, [addToast]);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setPendingCount(getPendingMutationCount());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial state
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return (
    <AnimatePresence>
      {!isOnline && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="w-full overflow-hidden"
          style={{ backgroundColor: 'var(--warning)', color: 'var(--text-inverse)' }}
        >
          <div className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="1" y1="1" x2="23" y2="23"></line>
              <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
              <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
              <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
              <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
              <line x1="12" y1="20" x2="12.01" y2="20"></line>
            </svg>
            <span>
              You're offline. Changes will sync when reconnected.
              {pendingCount > 0 && ` (${pendingCount} pending)`}
            </span>
            {isReplaying && (
              <span className="text-xs opacity-80">Syncing...</span>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
